import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from 'src/app/services/auth.service';
import { formatCurrency } from 'src/app/services/constants';
import {
  actionsForTarget,
  normalizeCreditActions,
  resolveCreditBounds,
  VesselFinderCreditAction,
} from 'src/app/services/operators/vesselfinder-credit-actions';

/**
 * VesselFinder API credit purchase.
 *
 * A near copy of `VoyageCreditPurchaseComponent`, swapping only the catalogue
 * import — DUPLICATED rather than parameterized (frontend plan §5.4's two
 * sanctioned options), because parameterizing the Voyage Radar component
 * would mean changing a live, already-shipped, price-facing dialog to accept
 * its credit-catalogue module as data rather than a static import, and that
 * refactor was judged not worth the regression risk to a working feature for
 * this pass. Revisit if a third credit-wallet operator arrives — same test
 * the client wizard applies to `ais/shared/`: prove three consumers first.
 *
 * Styles are NOT duplicated: `styleUrls` points at the Voyage Radar
 * component's stylesheet directly. Every class name and CSS variable in it is
 * already generic (`.vcp__*`, `--vcp-*`) — nothing there says "Voyage Radar" —
 * so this is the same "one definition, not a copy that can drift" reasoning
 * that keeps `app-voyage-credit-catalogue` itself shared below.
 *
 * The result contract is identical to Voyage Radar's dialog so the caller in
 * `taskcost.component.ts` needs no branch beyond which component to open:
 * `{ apiCredits }` on buy, the string 'close' on cancel.
 */
@Component({
  selector: 'app-vesselfinder-credit-purchase',
  templateUrl: './vesselfinder-credit-purchase.component.html',
  styleUrls: ['../voyage-credit-purchase/voyage-credit-purchase.component.scss'],
})
export class VesselFinderCreditPurchaseComponent implements OnInit {
  creditFormGroup: FormGroup;
  private creditsControl: FormControl;

  /** No AOI/POI narrowing for this operator in this phase — `actionsForTarget` is a pass-through. */
  creditActions: VesselFinderCreditAction[] = [];

  creditUnitPrice = 0;
  showCreditPricing = false;
  minCredits = 0;
  maxCredits: number | null = null;
  creditError = true;
  creditMessage = '';
  creditChips: number[] = [];

  private activeUser: any;

  constructor(
    private dialogRef: MatDialogRef<VesselFinderCreditPurchaseComponent>,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.authService.user.subscribe((user) => (this.activeUser = user));

    const row = this.data?.selectedRow;
    this.creditActions = actionsForTarget(normalizeCreditActions(row?.apiCreditActions), !!this.data?.isPointTarget);
    this.creditUnitPrice = Number(row?.unitcost ?? row?.latency?.[0]?.unitcost ?? 0);
    this.showCreditPricing = this.creditUnitPrice > 0;

    const bounds = resolveCreditBounds(row, this.creditActions);
    this.minCredits = bounds.min;
    this.maxCredits = bounds.max;
    this.creditChips = this.buildChips(bounds.min, bounds.max);

    this.creditsControl = new FormControl('');
    this.creditFormGroup = new FormGroup({ credits: this.creditsControl });
    this.creditFormGroup.valueChanges.subscribe(() => this.validate());
    this.validate();
  }

  private validate(): void {
    const raw = this.creditsControl.value;
    const blank = raw === '' || raw === null || raw === undefined;
    const value = Number(raw);

    if (blank) {
      this.creditError = true;
      this.creditMessage = '';
      return;
    }

    if (!Number.isFinite(value) || value % 1 !== 0 || value < 1) {
      this.creditError = true;
      this.creditMessage = 'Enter a whole number of credits.';
      return;
    }

    if (value < this.minCredits) {
      this.creditError = true;
      this.creditMessage = this.showCreditPricing
        ? `The minimum purchase is ${this.formatCredits(this.minCredits)} credits ` +
          `(${this.costLabel(this.minCredits * this.creditUnitPrice)}).`
        : `The minimum purchase is ${this.formatCredits(this.minCredits)} credits.`;
      return;
    }

    if (this.maxCredits !== null && value > this.maxCredits) {
      this.creditError = true;
      this.creditMessage =
        `The maximum purchase is ${this.formatCredits(this.maxCredits)} credits. ` +
        `Task again for more.`;
      return;
    }

    this.creditError = false;
    this.creditMessage = '';
  }

  private buildChips(min: number, max: number | null): number[] {
    const ceiling = max ?? min * 10;
    const between = [500, 1000, 2500].filter((n) => n > min && n < ceiling).slice(0, 2);
    const all = [min, ...between, ceiling];
    return [...new Set(all)].sort((a, b) => a - b);
  }

  setCredits(n: number): void {
    this.creditsControl.setValue(n);
  }

  get creditsEntered(): number {
    const v = Number(this.creditsControl?.value);
    return Number.isFinite(v) && v > 0 ? Math.trunc(v) : 0;
  }

  get creditTotalCost(): number {
    return this.creditUnitPrice * this.creditsEntered;
  }

  get hasSummary(): boolean {
    return !this.creditError && this.creditsEntered > 0;
  }

  formatCredits(n: number | null): string {
    return Number(n || 0).toLocaleString('en-US');
  }

  get rangeHint(): string {
    const min = this.formatCredits(this.minCredits);
    return this.maxCredits === null
      ? `${min} credits or more`
      : `${min} – ${this.formatCredits(this.maxCredits)} credits`;
  }

  get totalLabel(): string {
    if (!this.hasSummary) return '—';
    return this.showCreditPricing ? this.costLabel(this.creditTotalCost) : `${this.formatCredits(this.creditsEntered)} credits`;
  }

  get totalSubLabel(): string {
    if (!this.hasSummary) return 'Choose or enter an amount.';
    return this.showCreditPricing ? `${this.formatCredits(this.creditsEntered)} credits` : 'Ready to buy.';
  }

  get buyLabel(): string {
    return 'Buy credits';
  }

  costLabel(cost: number): string {
    if (this.activeUser?.pointsEligible && this.activeUser?.sensorConversionFactor) {
      const [part1, part2] = this.activeUser.sensorConversionFactor.split(':').map(parseFloat);
      const conversionFactor = part2 / part1;
      return `${Math.round(cost * conversionFactor)} Points`;
    }
    return formatCurrency(cost);
  }

  buyCredits(): void {
    if (this.creditError) return;
    this.dialogRef.close({ apiCredits: this.creditsEntered });
  }

  onNoClick(): void {
    this.dialogRef.close('close');
  }
}
